import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';
import { useTranslation } from 'react-i18next';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Maintenance & Services Télécoms — IS TELECOM',
  description: 'Maintenance préventive et curative, support NOC 24/7, gestion des pièces détachées. Équipes certifiées Huawei, Ericsson, Nokia. SLA garanti 4h.',
  provider: {
    '@type': 'Organization',
    name: 'IS TELECOM',
    url: 'https://is-telecom.co',
  },
  areaServed: 'Afrique, Asie, Europe, Amériques',
};

export default function TelecomMaintenance() {
  const { t } = useTranslation();

  const services = [
    {
      title: 'Maintenance Préventive',
      desc: 'Interventions programmées sur vos sites : vérification des équipements radio, nettoyage, ajustement des antennes, contrôle des systèmes d\'alimentation et des climatisations. Planning personnalisé selon vos contraintes opérationnelles.',
      icon: 'ri-calendar-check-line',
      image: 'https://readdy.ai/api/search-image?query=Black%20African%20telecom%20maintenance%20engineer%20with%20dark%20skin%20performing%20preventive%20inspection%20on%20radio%20equipment%20at%20base%20station%2C%20professional%20Black%20technician%20with%20diagnostic%20tools%20checking%20network%20hardware%2C%20organized%20equipment%20racks%20with%20cables%2C%20bright%20clean%20industrial%20setting&width=800&height=500&seq=tel-prev-01&orientation=landscape&nocache=true',
    },
    {
      title: 'Maintenance Curative 24/7',
      desc: 'Dépannage rapide avec SLA garanti sous 4h pour les sites critiques. Nos équipes interviennent jour et nuit pour restaurer la connectivité. Gestion des incidents avec ticketing et suivi en temps réel.',
      icon: 'ri-time-line',
      image: 'https://readdy.ai/api/search-image?query=Emergency%20telecom%20repair%20team%20of%20Black%20African%20technicians%20with%20dark%20skin%20working%20at%20night%20with%20vehicle%20lighting%2C%20professional%20dark-skinned%20African%20engineers%20repairing%20cellular%20base%20station%20after%20outage%2C%20dramatic%20nighttime%20industrial%20photography&width=800&height=500&seq=tel-cur-01&orientation=landscape&nocache=true',
    },
    {
      title: 'NOC — Network Operations Center',
      desc: 'Centre d\'opérations réseau en direct : supervision de la performance, alertes automatiques, gestion proactive des incidents. Tableaux de bord temps réel et rapports KPI mensuels pour une visibilité totale sur votre infrastructure.',
      icon: 'ri-dashboard-3-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20network%20operations%20center%20with%20large%20monitoring%20screens%2C%20Black%20African%20engineers%20with%20dark%20skin%20at%20workstations%20supervising%20cellular%20network%20performance%2C%20high-tech%20NOC%20control%20room%20with%20multiple%20displays%2C%20professional%20technology%20environment%20with%20blue%20ambient%20lighting%2C%20dark-skinned%20professionals&width=800&height=500&seq=tel-noc-01&orientation=landscape&nocache=true',
    },
    {
      title: 'Gestion des Pièces Détachées',
      desc: 'Stocks tampons stratégiques dans nos 6 pays d\'intervention. Catalogue complet des pièces Huawei, Ericsson, Nokia. Remplacement express des modules défaillants pour minimiser le temps d\'indisponibilité.',
      icon: 'ri-archive-line',
      image: 'https://readdy.ai/api/search-image?query=Organized%20telecom%20spare%20parts%20warehouse%20with%20shelves%20of%20network%20equipment%20modules%20and%20radio%20components%2C%20professional%20inventory%20management%20for%20telecommunications%20maintenance%2C%20clean%20industrial%20storage%20facility%20with%20labeled%20boxes%20and%20equipment%20racks%2C%20bright%20interior%20lighting&width=800&height=500&seq=tel-spare-01&orientation=landscape',
    },
  ];

  const stats = [
    { value: '99.8', suffix: '%', label: 'Disponibilité réseau' },
    { value: '4', suffix: 'h', label: 'SLA intervention' },
    { value: '12', suffix: '', label: 'Pays avec stock tampon' },
    { value: '5000', suffix: '+', label: 'Sites en maintenance' },
  ];

  const slas = [
    { tier: 'Bronze', response: '8h', availability: '99.5%', desc: 'Pour sites non critiques avec supervision distante.' },
    { tier: 'Silver', response: '4h', availability: '99.8%', desc: 'Pour sites urbains et zones à forte densité.' },
    { tier: 'Gold', response: '2h', availability: '99.9%', desc: 'Pour sites stratégiques et centres de données.' },
    { tier: 'Platinum', response: '1h', availability: '99.95%', desc: 'Pour sites critiques avec équipe dédiée sur place.' },
  ];

  return (
    <Layout>
      <JsonLd data={schema} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-background-950">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20network%20operations%20center%20with%20glowing%20screens%20and%20data%20visualization%2C%20dark%20control%20room%20with%20blue%20and%20green%20digital%20displays%20showing%20telecom%20network%20status%2C%20professional%20NOC%20environment%20with%20Black%20African%20operators%20with%20dark%20skin%20at%20workstations%2C%20dark-skinned%20engineers%20in%20futuristic%20technology%20monitoring%20center&width=1920&height=900&seq=tel-maint-hero-01&orientation=landscape&nocache=true"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/70 to-background-950/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-accent-400/50 rounded-full px-5 py-2.5 mb-8">
            <i className="ri-tools-line text-accent-400 text-sm w-4 h-4 flex items-center justify-center" />
            <span className="text-accent-300 text-sm font-medium uppercase tracking-wider">Télécommunications</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-white mb-6 leading-[1.1]">
            Maintenance &<br />
            <span className="text-accent-400">Services NOC</span>
          </h1>
          <p className="text-lg md:text-xl text-background-200 max-w-2xl mx-auto leading-relaxed mb-10">
            Support technique 24/7, maintenance préventive et curative, gestion des pièces détachées.
            Votre réseau ne s&apos;arrête jamais.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold font-heading text-accent-400 mb-1">
                  {s.value}<span className="text-2xl">{s.suffix}</span>
                </div>
                <div className="text-background-300 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <ScrollReveal variant="fade-up">
        <section className="py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Nos Services</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                Un réseau toujours opérationnel
              </h2>
              <p className="text-foreground-500 mt-4 max-w-2xl mx-auto">
                Notre approche combine maintenance proactive, réactivité d&apos;intervention et supervision permanente pour une disponibilité maximale.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((svc) => (
                <div key={svc.title} className="group bg-white rounded-3xl overflow-hidden border border-background-200/70 hover:shadow-xl transition-all duration-500">
                  <div className="h-56 overflow-hidden">
                    <img src={svc.image} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                        <i className={`${svc.icon} text-primary-600 text-xl w-6 h-6 flex items-center justify-center`} />
                      </div>
                      <h3 className="text-xl font-bold font-heading text-foreground-950">{svc.title}</h3>
                    </div>
                    <p className="text-foreground-500 text-sm leading-relaxed">{svc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SLA Tiers */}
      <ScrollReveal variant="fade-up" delay={100}>
        <section className="py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center border border-primary-300/60 rounded-full px-4 py-1.5 mb-6">
                <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">Contrats de Service</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-heading text-foreground-950 max-w-3xl mx-auto leading-tight">
                Des SLA adaptés à vos besoins
              </h2>
              <p className="text-foreground-500 mt-4 max-w-2xl mx-auto">
                Quatre niveaux de service pour correspondre à la criticité de chaque site de votre réseau.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {slas.map((sla) => (
                <div key={sla.tier} className="bg-white rounded-3xl p-6 border border-background-200/70 hover:shadow-md transition-shadow">
                  <div className="inline-flex items-center border border-primary-300/60 rounded-full px-3 py-1 mb-4">
                    <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">{sla.tier}</span>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">Temps de réponse</span>
                      <span className="font-semibold text-foreground-950">{sla.response}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">Disponibilité</span>
                      <span className="font-semibold text-foreground-950">{sla.availability}</span>
                    </div>
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed">{sla.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA */}
      <section className="py-24 bg-background-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4">
            Protégez votre réseau
          </h2>
          <p className="text-lg text-background-300 max-w-3xl mx-auto mb-8">
            Confiez-nous la maintenance de vos sites et concentrez-vous sur votre cœur de métier.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Demander un devis
              <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-transparent border-2 border-primary-400 text-primary-400 hover:bg-primary-500/10 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}